/**
 * Notification Preferences Hooks - Issue #319
 * 
 * React Query hooks for managing user notification preferences
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  notificationsApi,
  type NotificationPreferences,
  type UpdateNotificationPreferencesRequest,
  type NotificationPreferencesResponse,
  type SendTestNotificationResponse
} from '../lib/api/notifications';
import { useOroMutation, OroOperation } from './useOroMutation';

// Re-export types for convenience
export type { NotificationPreferences, UpdateNotificationPreferencesRequest, NotificationPreferencesResponse, SendTestNotificationResponse };

// Get user's notification preferences
export function useNotificationPreferences() {
  return useQuery({
    queryKey: ['notification-preferences'],
    queryFn: () => notificationsApi.getPreferences(),
  });
}

// Update notification preferences
export function useUpdateNotificationPreferences() {
  const queryClient = useQueryClient();

  return useOroMutation<NotificationPreferencesResponse, UpdateNotificationPreferencesRequest>({
    mutationFn: (data: UpdateNotificationPreferencesRequest) =>
      notificationsApi.updatePreferences(data),
    onSuccess: () => {
      // Invalidate preferences query to refetch
      queryClient.invalidateQueries({ queryKey: ['notification-preferences'] });
    },
    successToast: 'Notification settings updated',
    oro: {
      op: OroOperation.UpdateNotificationPreferences,
      retryPolicy: 'never',
      debugLabel: 'Notification settings form',
    },
  });
}

// Send test notification email (#837)
export function useSendTestNotification() {
  return useOroMutation<SendTestNotificationResponse, void>({
    mutationFn: () => notificationsApi.sendTestNotification(),
    successToast: 'Test email sent! Check your inbox.',
    oro: {
      op: OroOperation.SendTestNotification,
      retryPolicy: 'never',
      debugLabel: 'Test notification button',
    },
  });
}